/*!
 * (C) Ionic http://ionicframework.com - MIT License
 */

import {
    Component,
    ElementRef,
    HostBinding,
    OnInit,
    ViewEncapsulation,
    inject,
    ChangeDetectionStrategy,
    input,
} from '@angular/core';

/**
 * @part track - The background track of the toggle.
 * @part handle - The toggle handle, or knob, used to change the checked state.
 */
const sharedStylesheet = new CSSStyleSheet();
sharedStylesheet.replaceSync(
    `:host { box-sizing: content-box !important; display: inline-block; position: relative; width: var(--toggle-width); height: var(--toggle-height); outline: none; contain: content; cursor: pointer; touch-action: none; user-select: none; z-index: 2; } :host(.bazis-toggle--disabled) { pointer-events: none; } label { left: 0; top: 0; margin-left: 0; margin-right: 0; margin-top: 0; margin-bottom: 0; position: absolute; width: 100%; height: 100%; border: 0; background: transparent; cursor: pointer; appearance: none; outline: none; display: flex; align-items: center; opacity: 0; pointer-events: none; } label::-moz-focus-inner { border: 0; } [dir=rtl] label, :host-context([dir=rtl]) label { left: unset; right: unset; right: 0; } input { position: absolute; top: 0; left: 0; right: 0; bottom: 0; width: 100%; height: 100%; margin: 0; padding: 0; border: 0; outline: 0; clip: rect(0 0 0 0); opacity: 0; overflow: hidden; -webkit-appearance: none; -moz-appearance: none; } .bazis-toggle__track-wrapper { display: flex; position: relative; align-items: center; width: 100%; height: 100%; transition: var(--handle-transition); } .bazis-toggle__track { box-sizing: border-box; display: block; position: relative; width: 100%; height: 100%; background: var(--background); border-radius: var(--border-radius); border: var(--border-width) var(--border-style) var(--border-color); pointer-events: none; overflow: inherit; } :host(.bazis-toggle--checked) .bazis-toggle__track { --border-width: var(--border-width-checked, var(--border-width)); --border-style: var(--border-style-checked, var(--border-style)); background: var(--background-checked); border-color: var(--border-color-checked); } :host(.bazis-toggle--disabled) .bazis-toggle__track { background: var(--background-disabled, var(--background)); } :host(.bazis-toggle--disabled.bazis-toggle--checked) .bazis-toggle__track { background: var(--background-checked-disabled, var(--background-checked)); } .bazis-toggle__handle { left: var(--handle-spacing); border-radius: var(--handle-border-radius); position: absolute; width: var(--handle-width); height: var(--handle-height); max-height: var(--handle-max-height); transition: var(--handle-transition); background: var(--handle-background); box-shadow: var(--handle-box-shadow); contain: strict; } [dir=rtl] .bazis-toggle__handle, :host-context([dir=rtl]) .bazis-toggle__handle { left: unset; right: unset; right: var(--handle-spacing); } :host(:active) .bazis-toggle__handle { width: var(--handle-width-active, var(--handle-width)); } :host(:hover) .bazis-toggle__track { background: var(--background-hover, var(--background)); } :host(.bazis-toggle--checked:hover) .bazis-toggle__track { background: var(--background-checked-hover, var(--background-checked)); } :host(.bazis-toggle--checked) .bazis-toggle__handle { transform: translateX(calc(var(--toggle-width) - var(--handle-width) - var(--handle-spacing) * 2)); background: var(--handle-background-checked); } :host-context([dir=rtl]):host(.bazis-toggle--checked) .bazis-toggle__handle, :host-context([dir=rtl]).bazis-toggle--checked .bazis-toggle__handle { transform: translateX(calc((var(--toggle-width) - var(--handle-width) - var(--handle-spacing) * 2) * -1)); } :host(.bazis-toggle--checked:active) .bazis-toggle__handle { transform: translateX(calc(var(--toggle-width) - var(--handle-width-active, var(--handle-width)) - var(--handle-spacing) * 2)); } :host-context([dir=rtl]):host(.bazis-toggle--checked:active) .bazis-toggle__handle, :host-context([dir=rtl]).bazis-toggle--checked:active .bazis-toggle__handle { transform: translateX(calc((var(--toggle-width) - var(--handle-width-active, var(--handle-width)) - var(--handle-spacing) * 2) * -1)); } :host(.bazis-toggle--disabled) .bazis-toggle__handle { background: var(--handle-background-disabled, var(--handle-background)); } :host(.bazis-toggle--disabled.bazis-toggle--checked) .bazis-toggle__handle { background: var(--handle-background-checked-disabled, var(--handle-background-checked)); } :host(.bazis-color) .bazis-toggle__track { --background-checked: var(--bazis-color-base); --background-checked-hover: var(--bazis-color-tint); }`,
);

@Component({
    selector: 'bazis-toggle',
    template: `
        <div
            class="bazis-toggle__track"
            part="track"
        >
            <div class="bazis-toggle__track-wrapper">
                <div
                    class="bazis-toggle__handle"
                    part="handle"
                ></div>
            </div>
        </div>
        <label [for]="inputId"></label>
        <input
            type="checkbox"
            role="switch"
            [checked]="checked()"
            [id]="inputId"
        />
    `,
    // styleUrls: ['toggle.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false,
})
export class ToggleElement implements OnInit {
    public el = inject(ElementRef);

    readonly disabled = input<boolean>();

    readonly checked = input<boolean>();

    readonly color = input<string>();

    @HostBinding('class') get toggleStyle() {
        return {
            'bazis-toggle--checked': this.checked(),
            'bazis-toggle--disabled': this.disabled(),
        };
    }

    @HostBinding('ariaChecked') get toggleChecked() {
        return this.checked();
    }

    @HostBinding('ariaHidden') get toggleHidden() {
        return this.disabled();
    }

    inputId: string = `toggleId`;

    ngOnInit(): void {
        const host = this.el.nativeElement;
        if (host && host.shadowRoot) {
            const shadowRoot = host.shadowRoot;
            const styles = shadowRoot.querySelectorAll('style');
            styles.forEach((style) => style.remove());
            shadowRoot.adoptedStyleSheets = [sharedStylesheet];
        }

        // this.disabled = this.disabled !== undefined && this.disabled !== false;
        // this.checked = this.checked !== undefined && this.checked !== false;
    }
}
